/**
 * Formats d'étiquettes thermiques courants (marché US pouces + Europe mm).
 * Choisir le même format ici et dans le driver Chrome / 4BARCODE.
 */

import type { LabelPrintFormat, LabelPrintFormatGroup } from "./types.js";

/** Ordre des groupes dans la liste déroulante. */
export const LABEL_PRINT_FORMAT_GROUPS: LabelPrintFormatGroup[] = [
  { id: "4in", label: "4″ largeur (101,6 mm) — produit & expédition" },
  { id: "3in", label: "3″ largeur (76,2 mm)" },
  { id: "2in", label: "2″ largeur (50,8 mm)" },
  { id: "europe", label: "Europe & pharmacie (mm)" },
  { id: "narrow", label: "Étroit / ruban & retail" },
  { id: "driver", label: "Presets driver 4BARCODE" },
];

const IN = 25.4;

function inchFormat(
  id: string,
  wIn: number,
  hIn: number,
  group: string,
  extra?: string,
): LabelPrintFormat {
  const w = Math.round(wIn * IN * 10) / 10;
  const h = Math.round(hIn * IN * 10) / 10;
  const inchLabel = `${wIn} × ${hIn}″`;
  const mmLabel = `${w} × ${h} mm`;
  const suffix = extra ? ` — ${extra}` : "";
  return {
    id,
    group,
    label: `${inchLabel} — ${mmLabel}${suffix}`,
    driverName: `${wIn} x ${hIn} (${w} mm x ${h} mm)`,
    widthMm: w,
    heightMm: h,
  };
}

function mmFormat(
  id: string,
  w: number,
  h: number,
  group: string,
  name: string,
): LabelPrintFormat {
  return {
    id,
    group,
    label: `${name} — ${w} × ${h} mm`,
    driverName: `${name} (${w} mm x ${h} mm)`,
    widthMm: w,
    heightMm: h,
  };
}

export const LABEL_PRINT_FORMATS: LabelPrintFormat[] = [
  inchFormat("4x1", 4, 1, "4in"),
  inchFormat("4x1.5", 4, 1.5, "4in"),
  inchFormat("4x2", 4, 2, "4in"),
  inchFormat("4x2.5", 4, 2.5, "4in"),
  inchFormat("4x3", 4, 3, "4in", "souvent idéal produit"),
  inchFormat("4x3.5", 4, 3.5, "4in"),
  inchFormat("4x4", 4, 4, "4in"),
  inchFormat("4x5", 4, 5, "4in"),
  inchFormat("4x6", 4, 6, "4in"),

  inchFormat("3x1", 3, 1, "3in"),
  inchFormat("3x2", 3, 2, "3in"),
  inchFormat("3x3", 3, 3, "3in"),
  inchFormat("3x4", 3, 4, "3in"),
  inchFormat("3x5", 3, 5, "3in"),

  inchFormat("2x1", 2, 1, "2in"),
  inchFormat("2x2", 2, 2, "2in"),
  inchFormat("2x2.5", 2, 2.5, "2in"),
  inchFormat("2x3", 2, 3, "2in"),
  inchFormat("2x4", 2, 4, "2in"),

  mmFormat("58x40", 58, 40, "europe", "58×40 pharmacie"),
  {
    id: "60x40-180",
    group: "europe",
    label: "60×40 pharmacie — rouleau centré 180 mm (preset 4BARCODE)",
    driverName: "60 x 40 (60 mm x 40 mm) sur rouleau 180 mm",
    widthMm: 60,
    heightMm: 40,
    rollWidthMm: 180,
    rollOffsetFactor: 8 / 60,
  },
  mmFormat("62x40", 62, 40, "europe", "62×40 compact"),
  mmFormat("62x100", 62, 100, "europe", "62×100"),
  mmFormat("70x50", 70, 50, "europe", "70×50"),
  mmFormat("75x40", 75, 40, "europe", "75×40 pharmacie"),
  mmFormat("75x45", 75, 45, "europe", "75×45 pharmacie"),
  mmFormat("75x48", 75, 48, "europe", "75×48 pharmacie"),
  mmFormat("80x50", 80, 50, "europe", "80×50"),
  mmFormat("80x60", 80, 60, "europe", "80×60"),
  mmFormat("100x50", 100, 50, "europe", "100×50"),
  mmFormat("100x100", 100, 100, "europe", "100×100"),
  mmFormat("100x150", 100, 150, "europe", "100×150"),

  mmFormat("58x30", 58, 30, "narrow", "58×30 prix"),
  mmFormat("80x30", 80, 30, "narrow", "80×30 pharmacie"),
  mmFormat("62x29", 62, 29, "narrow", "62×29 gondole"),
  mmFormat("89x28", 89, 28, "narrow", "89×28 adresse"),
  mmFormat("89x36", 89, 36, "narrow", "89×36"),
  mmFormat("99x57", 99, 57, "narrow", "99×57 expédition"),
  inchFormat("1.2x7.1", 1.2, 7.1, "narrow", "ruban continu"),

  {
    id: "user-76x130",
    group: "driver",
    label: "USER — 76 × 130 mm",
    driverName: "USER (76,0 mm x 130,0 mm)",
    widthMm: 76,
    heightMm: 130,
  },
  {
    id: "2.8x5",
    group: "driver",
    label: "2.8 × 5″ — 72 × 130 mm",
    driverName: "2.8 x 5 (72,0 mm x 130,0 mm)",
    widthMm: 72,
    heightMm: 130,
  },
  {
    id: "3.2x2.5",
    group: "driver",
    label: "3.2 × 2.5″ — 80 × 63,5 mm",
    driverName: "3.2 x 2.5 (80,0 mm x 63,5 mm)",
    widthMm: 80,
    heightMm: 63.5,
  },
];

/** Format par défaut — USER driver ; pour rouleau 4″ essayer « 4×3″. */
export const DEFAULT_LABEL_PRINT_FORMAT_ID = "user-76x130";

export const LABEL_CONTENT_WIDTH_MM = 62;
export const LABEL_CONTENT_HEIGHT_MM = 40;

export function getLabelPrintFormat(id: string): LabelPrintFormat | undefined {
  return LABEL_PRINT_FORMATS.find((f) => f.id === id);
}

export function getGroupedLabelPrintFormats(): Array<
  LabelPrintFormatGroup & { formats: LabelPrintFormat[] }
> {
  return LABEL_PRINT_FORMAT_GROUPS.map((group) => ({
    ...group,
    formats: LABEL_PRINT_FORMATS.filter((f) => f.group === group.id),
  })).filter((g) => g.formats.length > 0);
}
